import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  Input,
  Button,
  Space,
  message,
  DatePicker,
  Radio
} from "antd";
import { HomeOutlined , CheckOutlined, } from "@ant-design/icons";
import './Edit.scss'
import http from "@/utils/http";
import api from "@/utils/api";
import { FileDetail, CustomUploadNew } from "@/hocComponents";
import dayjs from "dayjs";
import commonFn from "@/utils/commonFn";

const ContractorAddModal = forwardRef(({
  submitInitFn,
  id
}, ref) => {
    const [form] = Form.useForm();
    const [btnLoading, setBtnLoading] = useState(false);
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [filePaths, setFilePaths] = useState([]);
    const [managerIdList, setManagerIdList] = useState([]);
    const [projectList, setProjectList] = useState([]);
    const [vendorList, setVendorList] = useState([]);
    const [vendorOption, setVendorOption] = useState({});
    const [propertyList, setPropertyList] = useState([]);
    const [unitIdList, setUnitIdList] = useState([]);
    const [isProjectKey, setIsProjectKey] = useState(0);
    const [propIdKey, setPropIdKey] = useState(0);
    const [unitIdKey, setUnitIdKey] = useState(0);

    const [type, setType] = useState('add');
    const [modalName, setModalName] = useState('Major Project');
    const [modalFlag, setModalFlag] = useState(false);
    const [detail, setDetail] = useState(null);
    const customUploadNewRef = useRef(null);

    useImperativeHandle(ref, () => ({
      openModal: (type, modalName) => {
        setModalFlag(true)
        setType(type)
        setModalName(modalName)
      }
    }));

    const selcetChangeFn = async (option, key) => {
      setVendorOption(option);
      form.setFieldsValue({ [key]: option[key] });
    };

    const getVendorList = async (content = '') => {
      const response = await http.get(api.vendorSearch, {params: {content}});
      if (response.code === "1") {
        const list = (response.data || []).map((item) => ({
          value: item.vendid,
          label: item.vendorName,
          vendorId: item.vendorId,
        }));
        setVendorList(list);
        return list
      }
      return []
    };

    const isGetPropertyListFn = (data) => {
      if(data && data?.length){
        const list = data?.map((item) => ({
          ...item,
          value: item.propId,
          label: item.propName,
        }));
        setPropertyList(list);
      } else {
        getPropertyList()
      }
    }

    const getPropertyList = async (content = '') => {
      const response = await http.get(api.getPropSearch, {params: {content}});
      if (response.code === "1") {
        const list = (response.data || []).map((item) => ({
          ...item,
          value: item.propId,
          label: item.propName,
        }));
        setPropertyList(list);
      }
    };

    const getManagerIdList = async (content = '') => {
      const response = await http.get(api.searchMajorAccount, {params: {content}});
      if (response.code === "1") {
        const list = (response.data || []).map((item) => ({
          value: item.accountId,
          label: item.roleAllName,
        }));
        setManagerIdList(list);
      }
    };

    const unitIdFun = async ({content = '', propId = form.getFieldValue().propId} = {}) => {
      if(!propId){
        message.warning("Please Select Property!");
        return
      }
      const response = await http.get(api.unitSearch, { params: {content, propId} });
      const list = [{ unitId: -1, unitName: "Common Area" }, ...(response?.data || [])]?.map((item) => ({
        value: item.unitId,
        label: item.unitName,
      }));
      setUnitIdList(list);
    };

    const getProjectList = async (projectName = '', propIdTemp, unitIdTemp) => {
      const propId = propIdTemp || form.getFieldValue()?.propId || ''
      const unitId = unitIdTemp || form.getFieldValue()?.unitId || ''
      if(!propId){
        message.warning("Please Select Property!");
        // commonFn.modalFailedFn({msg: "Please Select Property!"})
        return
      }
      const response = await http.get(api.getProjectList, {params: {propId, unitId, projectName}});
      if (response.code === "1") {
        const list = (response.data || []).map((item) => ({
          value: item.id,
          label: item.projectIdStr,
          projectManger: item.projectManger,
          managerId: item.managerId
        }));
        setProjectList(list);
      }
    };

    const projectChangeFn = (e, item) => {
      form.setFieldsValue({ projectManger: item?.projectManger || undefined })
    }

    useEffect(() => {
      if(form.getFieldValue().propId && ['add', 'approve', 'edit'].includes(type)){
        form.setFieldsValue({ unitId: undefined, id: undefined, projectManger: undefined });
        unitIdFun()
       if(form.getFieldValue().isProject === 2){
          getProjectList()
        }
      }
    },[propIdKey])

    useEffect(() => {
      if(form.getFieldValue().propId && ['add', 'approve', 'edit'].includes(type) && form.getFieldValue().isProject === 2){
        form.setFieldsValue({ id: undefined, projectManger: undefined });
        getProjectList()
      }
    },[unitIdKey])

    useEffect(() => {
      if(form.getFieldValue().propId && ['add', 'approve', 'edit'].includes(type) && form.getFieldValue().isProject === 2){
        getProjectList()
      }
    },[isProjectKey])

    const afterOpenChangeFn = (flag) => {
      if(flag){
        if(type === 'add'){
          getVendorList()
          getPropertyList()
          getManagerIdList()
        }else{
          getDetailFn()
        }
      }else{
        setDetail(null)
        setUnitIdList([])
        setVendorList([])
        setPropertyList([])
        setVendorOption([])
        setProjectList([])
        setBtnLoading(false);
        setBtnDisabled(false);
        form.resetFields();
      }
    }

    const getDetailFn = async() => {
      setBtnDisabled(true)
      const response = await http.get(api.getContractDetail, { params: {id} });
      if (response.code === "1") {
        const {contract, project} = response.data
        const list = (contract?.fileList || [])?.map(item => ({ ...item, url: item.allPath, baseUrl: item.allPath, name: item.name || item.fileName, type: item.contentType}))
        const { propId, unitId, unitName, propName, projectName, managerId, projectManger } = project
        const { isProject, vendorId, contractor, amount, commenceDate, expectedCompleteTime, contractDate, fileList, refuseReason, createType, projectIdStr, approveType } = contract
        const detailTemp = {
          showPropUnitFlag: project?.id && !propId,
          propId,
          'contractId': contract?.id,
          'projectId': project?.id,
          'id': !propId ? undefined : project?.id,
          unitId,
          unitName,
          propName,
          projectName,
          managerId,
          projectManger,
          isProject,
          vendorId,
          contractor,
          amount,
          'commenceDate': dayjs(commenceDate),
          'expectedCompleteTime': dayjs(expectedCompleteTime),
          'contractDate': dayjs(contractDate),
          'File': fileList,
          refuseReason,
          'approveType':type === 'approve' ? undefined : approveType,
          createType,
          'fileList': list
        }
        if(propId){
          unitIdFun({propId})
        }
        const vendorListTemp = await getVendorList(contract?.contractor)
        const option = vendorListTemp.find(item => item?.vendorId === contract?.vendorId)
        selcetChangeFn(option, '')
        getManagerIdList()
        if(['edit', 'view'].includes(type)){
          getPropertyList()
          isGetPropertyListFn(project?.propInfoList)
          if(isProject === 2){
            propId && getProjectList('', propId, unitId)
          }
        }else{
          isGetPropertyListFn(project?.propInfoList)
        }
        setDetail(detailTemp)
        form.setFieldsValue({
          ...detailTemp,
        });
        if(['approve', 'edit'].includes(type) && customUploadNewRef.current){
          customUploadNewRef.current.initUpdateList(list)
        }
        setRefuseReasonFlag(contract?.approveType === 3)
      }
      setBtnDisabled(false)
    }

    const testFn = async (values) => {
      const formData = form.getFieldsValue();
      console.log("====formData", formData);
  
    }

    
    const [refuseReasonFlag, setRefuseReasonFlag] = useState(false);

    const approvalTypeChangeFn = (e) => {
      if (e.target.value === 2) {
        form.setFieldValue('refuseReason', '')
        setRefuseReasonFlag(false)
      }else{
        setRefuseReasonFlag(true)
      }
    }
    
    const submitAddFn = async () => {
      setBtnLoading(true)
      const { isProject, managerId, propId, unitId, projectName, id, vendorId, amount, commenceDate, expectedCompleteTime, contractDate} = form.getFieldValue();

      let project = {id}
      if(isProject === 1){
        project = {
          managerId,
          propId,
          unitId,
          projectName,
        }
      }
      let params = {
        project,
        contract: {
          vendorId,
          commenceDate: commenceDate.format('YYYY-MM-DD') || '',
          expectedCompleteTime: expectedCompleteTime.format('YYYY-MM-DD') || '',
          contractDate: contractDate.format('YYYY-MM-DD') || '',
          commenceDateStr: commenceDate.format('YYYY-MM-DD') || '',
          expectedCompleteTimeStr: expectedCompleteTime.format('YYYY-MM-DD') || '',
          contractDateStr: contractDate.format('YYYY-MM-DD') || '',
          isProject,
          amount,
        },
        fileList: filePaths
      }
      const response = await http.post(api.saveContractProject, params);
      if (response.code === "1") {
        message.success("New successfully"); // 成功提示
        setModalFlag(false)
        submitInitFn();
      } else {
        commonFn.modalFailedFn(response)
      }
      setBtnLoading(false)
    }

    const submitApproveFn = async(idTemp) => {
      setBtnLoading(true)
      const { isProject, managerId, propId, unitId, projectName, id, amount, commenceDate, expectedCompleteTime, contractDate, approveType, refuseReason} = form.getFieldValue();
      let project = {id: idTemp || id}
      if(isProject === 1){
        project = {
          id: detail?.projectId,
          managerId,
          propId,
          unitId,
          projectName,
        }
      }
      let params = {
        project,
        contract: {
          id: detail?.contractId,
          commenceDate: commenceDate.format('YYYY-MM-DD') || '',
          expectedCompleteTime: expectedCompleteTime.format('YYYY-MM-DD') || '',
          contractDate: contractDate.format('YYYY-MM-DD') || '',
          commenceDateStr: commenceDate.format('YYYY-MM-DD') || '',
          expectedCompleteTimeStr: expectedCompleteTime.format('YYYY-MM-DD') || '',
          contractDateStr: contractDate.format('YYYY-MM-DD') || '',
          isProject,
          amount,
          createType: detail?.createType,
          approveType,
          refuseReason
        },
        fileList: filePaths.filter(item => !item.id)
      }
      const response = await http.post(api.updateApprove, params);
      if (response.code === "1") {
        setModalFlag(false)
        submitInitFn();
      } else {
        commonFn.modalFailedFn(response)
      }
      setBtnLoading(false)
    }

    const submitEditFn = async() => {
      setBtnLoading(true)
      const { isProject, managerId, propId, unitId, projectName, id, amount, commenceDate, expectedCompleteTime, contractDate} = form.getFieldValue();
      let project = {id}
      if(isProject === 1){
        project = {
          managerId,
          propId,
          unitId,
          projectName,
        }
      }
      let params = {
        project,
        contract: {
          id: detail?.contractId,
          commenceDate: commenceDate.format('YYYY-MM-DD') || '',
          expectedCompleteTime: expectedCompleteTime.format('YYYY-MM-DD') || '',
          contractDate: contractDate.format('YYYY-MM-DD') || '',
          commenceDateStr: commenceDate.format('YYYY-MM-DD') || '',
          expectedCompleteTimeStr: expectedCompleteTime.format('YYYY-MM-DD') || '',
          contractDateStr: contractDate.format('YYYY-MM-DD') || '',
          isProject,
          amount,
        },
        fileList: filePaths.filter(item => !item.id)
      }
      const response = await http.post(api.updateContractProject, params);
      if (response.code === "1") {
        message.success("Updated successfully");
        setModalFlag(false)
        submitInitFn();
      } else {
        commonFn.modalFailedFn(response)
      }
      setBtnLoading(false)
    }

    const submitFn = async () => {
      if(type === 'approve' && form.getFieldValue()?.approveType === 3){
        submitApproveFn(detail?.projectId)
        return
      }
      form.validateFields().then(value => {
        if(type === 'add'){
          submitAddFn()
        }else if(type === 'approve'){
          submitApproveFn()
        }else if (type === 'edit'){
          submitEditFn()
        }
      }).catch(err =>{
        console.log(err,'===err');
      })
    }

    const deleteOldFn = async(file) => {
      let flag = false
      const response = await http.get(api.delContractFile, { params: {id: file.id} });
      if (response.code === "1") {
        flag = true
      }
      return flag
    }

  return (
    <Modal
      title={
        <div style={{ fontSize: "24px", fontWeight: "bold", color: "red" }}>
          <HomeOutlined style={{ marginRight: "8px" }} />
          {modalName}
          <div
            style={{ borderTop: "3px solid #3c8dbc", marginTop: "8px" }}
          ></div>
        </div>
      }
      className="AddModal"
      centered
      open={modalFlag}
      onCancel={() => setModalFlag(false)}
      footer={null}
      maskClosable={false}
      afterOpenChange={afterOpenChangeFn}
      width="80%"
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{span: 8}}
        wrapperCol={{span: 16}}
        labelWrap={true}
        labelAlign="right"
        onFinish={(values) => submitFn(values)}
      >
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <div
              style={{
                marginBottom: "16px",
                fontWeight: "bold",
                fontSize: "20px",
              }}
            >
              <HomeOutlined style={{ marginRight: "8px" }} />
              Major Project
            </div>

            { detail?.showPropUnitFlag && <Form.Item
              name=""
              className="showPropUnitFlag"
              label={null}
            >
              <div>{detail?.propName || ''}</div>
            </Form.Item> || null}
          
            <Form.Item
              label="Property"
              name="propId"
              rules={[{ required: true, message: 'Property is required!' }]}
            >
              <Select
                disabled={['view'].includes(type)}
                showSearch
                placeholder="Select a type"
                onChange={(e) => setPropIdKey(+new Date())}
                onSearch={getPropertyList}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={propertyList}
              />
            </Form.Item>
            
            {detail?.showPropUnitFlag && <Form.Item
              name=""
              className="showPropUnitFlag"
              label={null}
            >
              <div>{detail?.unitName || ''}</div>
            </Form.Item> || null}

            <Form.Item
                name="unitId"
                label="Unit"
                rules={[{ required: true, message: 'Unit is required!' }]}
              >
                <Select
                  disabled={['view'].includes(type)}
                  showSearch
                  placeholder="Select a type"
                  onChange={(e) => setUnitIdKey(+new Date())}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={unitIdList}
                />
              </Form.Item>
          
            {['add', 'edit', 'approve'].includes(type) && <Form.Item
              name="isProject"
              label="Is this a new project ?"
              rules={[{ required: true, message: 'Is this a new project is required!' }]}
            >
              <Select
                disabled={['view'].includes(type)}
                placeholder="Select a type"
                onChange={(e) => setIsProjectKey(+new Date())}
                options={[
                  {
                    value: 1,
                    label: "Yes",
                  },
                  {
                    value: 2,
                    label: "No",
                  }
                ]}
              />
            </Form.Item> || null}

            {form.getFieldValue().isProject === 1 && (
              <>
               {detail?.showPropUnitFlag && <Form.Item
                  name=""
                  className="showPropUnitFlag"
                  label={null}
                >
                  <div>{detail?.projectManger || ''}</div>
                </Form.Item> || null}
                <Form.Item
                  name="projectName"
                  label="Project Name"
                  rules={[{ required: true, message: 'Project Name is required!' }]}
                >
                  <Input
                    disabled={['view'].includes(type)}
                    placeholder="Please input Project Name"
                  />
                </Form.Item>

                <Form.Item
                  name="managerId"
                  label="Project Manger"
                  rules={[{ required: true, message: 'Project Manger is required!' }]}
                >
                  <Select
                    disabled={['view'].includes(type)}
                    showSearch
                    onSearch={getManagerIdList}
                    placeholder="Select a type"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={managerIdList}
                  />
                </Form.Item>
              </>
              ) || null
            }
            {form.getFieldValue().isProject === 2 && (
              <>
                <Form.Item
                  name="id"
                  label="Project #"
                  rules={[{ required: true, message: 'Project # is required!' }]}
                >
                  <Select
                    disabled={['view'].includes(type)}
                    showSearch
                    placeholder="Select a type"
                    onChange={(e, item) => projectChangeFn(e, item)}
                    onSearch={getProjectList}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={projectList}
                  />
                </Form.Item>
                {detail?.showPropUnitFlag && <Form.Item
                  name=""
                  className="showPropUnitFlag"
                  label={null}
                >
                  <div>{detail?.projectManger || ''}</div>
                </Form.Item> || null}
                <Form.Item
                  name="projectManger"
                  label="Project Manger"
                >
                <Input placeholder="Please input Project Manger" disabled />
                </Form.Item>

              </>
              ) || null
            }
            {vendorOption?.label && <Form.Item
              name=""
              className="showPropUnitFlag"
              label={null}
            >
              <div>{vendorOption?.label || ''}</div>
            </Form.Item> || null}

            <Form.Item
                name="vendorId"
                label="Contractor"
                rules={[{ required: true, message: 'Contractor is required!' }]}
              >
              <Select
                disabled={['approve', 'view'].includes(type) || detail?.createType === 2}
                allowClear
                showSearch
                placeholder="Select a type"
                onChange={(value, option) => selcetChangeFn(option, 'vendorId')}
                onSearch={(value) => {getVendorList(value)}}
                optionLabelProp={{ label: "value" }}
                value={vendorOption?.value}
                filterOption={(input, option) =>
                  (option?.label ?? "")?.toLowerCase().includes(input.toLowerCase())
                }
                options={vendorList}
              />
            </Form.Item>

             {commonFn.formInputNumberDoc({
                form,
                label: "Original Contract",
                disabled: ['view'].includes(type),
                allowNegative: false
              })}

            <Form.Item
              name="contractDate"
              label="Contract Date"
              rules={[{ required: true, message: "Contract Date is required!" }]}
            >
              <DatePicker
                disabled={['view'].includes(type)}
                style={{
                  width: "100%",
                }}
                format={['MM/DD/YYYY', "M/D/YYYY"]}
              />
            </Form.Item>

            <Form.Item
              name="commenceDate"
              label="Commence Date"
              rules={[{ required: true, message: "Commence Date is required!" }]}
            >
              <DatePicker
                disabled={['view'].includes(type)}
                style={{
                  width: "100%",
                }}
                format={['MM/DD/YYYY', "M/D/YYYY"]}
              />
            </Form.Item>

            <Form.Item
              name="expectedCompleteTime"
              label="Expected Complete Time"
              rules={[{ required: true, message: "Expected Complete Time is required!" }]}
            >
              <DatePicker
                disabled={['view'].includes(type)}
                style={{
                  width: "100%",
                }}
                format={['MM/DD/YYYY', "M/D/YYYY"]}
              />
            </Form.Item>

            {((type === 'view' && [2, 3].includes(detail?.approveType)) && detail?.createType ==2 || type === 'approve') && (
              <>
                <Form.Item
                  name="approveType"
                  label="Select"
                  rules={[{ required: true, message: "Please select an option!" }]}
                  validateTrigger="onChange"
                >
                  <Radio.Group onChange={approvalTypeChangeFn} disabled={type === 'view' || btnDisabled}>
                    <Radio value={2}>Agree</Radio>
                    <Radio value={3}>Refuse</Radio>
                  </Radio.Group>
                </Form.Item>
                {refuseReasonFlag && (
                  <Form.Item
                    label="Refuse Reason"
                    name="refuseReason" // 必须添加 name 属性
                  >
                    <Input.TextArea
                      disabled={type === 'view'}
                      autoSize={{ minRows: 3, maxRows: 10 }}
                    />
                  </Form.Item>
                )}
              </>
            )}

          </Col>
          <Col span={16}>
            <div
              style={{
                marginBottom: "16px",
                fontWeight: "bold",
                fontSize: "20px",
              }}
            >
              <HomeOutlined style={{ marginRight: "8px" }} />
              Upload Contract
            </div>
            <Form.Item labelCol={{ span: 0 }} wrapperCol={{ span: 24 }} name="uploadFile" label={null}>
              {['add', 'approve', 'edit'].includes(type) ? (
                <CustomUploadNew
                  ref={customUploadNewRef}
                  customConfig={{
                    accept: ".jpg, .jpeg, .png, .pdf",
                    status: 'add',
                    deleteOldFn: deleteOldFn
                  }}
                  setFilePaths={setFilePaths} 
                  filePaths={filePaths} 
                  setBtnDisabled={setBtnDisabled}
                  btnDisabled={btnDisabled}
                />
              ) : (
                <FileDetail
                  urlKey="allPath"
                  fileList={detail?.fileList || []}
                />
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <div style={{textAlign:'center'}}>
        <Space align="center">
          {/* <Button key="back" onClick={testFn}>
            TEST
          </Button> */}
          <Button key="back" onClick={() => setModalFlag(false)}>
            Cancel
          </Button>
          {type !== 'view' && <Button type="primary" onClick={() => submitFn()} loading={btnLoading} disabled={btnDisabled}>
            Submit
          </Button> || null}
        </Space>
      </div>
    </Modal>
  );
});

export default ContractorAddModal;
